import React, { useState } from 'react';
import {
  FileText, Target, User, BookOpen, Heart, Trophy,
  Sparkles, Plus, ChevronRight, CheckCircle, Clock,
  Edit3, Eye, Share2, Download, MoreHorizontal,
  GraduationCap, Users, Award, Calendar
} from 'lucide-react';

const EnhancedBragSheetMockup = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [showPreview, setShowPreview] = useState(false);

  const tabs = [
    {
      id: 'overview',
      label: 'Overview',
      icon: FileText,
      progress: 85,
      color: 'azure',
      count: '3/4'
    },
    {
      id: 'academic',
      label: 'Academic',
      icon: BookOpen,
      progress: 92,
      color: 'teal',
      count: '4/4'
    },
    {
      id: 'activities',
      label: 'Activities',
      icon: Users,
      progress: 78,
      color: 'indigo',
      count: '7/9'
    },
    {
      id: 'personal',
      label: 'Personal',
      icon: Heart,
      progress: 60,
      color: 'coral',
      count: '2/5'
    },
    {
      id: 'teacher',
      label: 'Teacher-Specific',
      icon: GraduationCap,
      progress: 40,
      color: 'gold',
      count: '1/3'
    }
  ];

  const mockData = {
    basicInfo: {
      name: 'Alex Chen',
      gradeLevel: '12th Grade',
      school: 'Lincoln High School',
      graduationYear: '2025',
      gpa: '4.2',
      classRank: '15',
      classSize: '487',
      testScores: '1480',
      apCourses: '12'
    }
  };

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-900 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 rounded-lg bg-azure-500 flex items-center justify-center">
              <FileText className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Brag Sheet Builder</h1>
              <p className="text-slate-600">Create professional recommendation materials</p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 text-sm text-slate-600">
              <Clock className="w-4 h-4" />
              Auto-saved 2 minutes ago
            </div>

            <button
              onClick={() => setShowPreview(!showPreview)}
              className="px-4 py-2 border border-slate-900 text-slate-900 rounded-lg hover:bg-slate-50 transition-colors flex items-center gap-2"
            >
              <Eye className="w-4 h-4" />
              Preview
            </button>

            <button className="px-4 py-2 bg-azure-500 text-white rounded-lg hover:bg-azure-600 transition-colors flex items-center gap-2">
              <Share2 className="w-4 h-4" />
              Share
            </button>

            <button className="p-2 border border-slate-900 text-slate-600 rounded-lg hover:bg-slate-50">
              <MoreHorizontal className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-6">
        <div className="grid grid-cols-12 gap-6">

          {/* Main Content */}
          <div className="col-span-8">

            {/* Progress Overview */}
            <div className="bg-white rounded-xl border border-slate-900 p-6 mb-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-slate-900">Completion Progress</h2>
                <span className="text-sm text-slate-600">Overall: 71% complete</span>
              </div>

              <div className="bg-stone-100 rounded-full h-2 mb-4">
                <div className="bg-azure-500 h-2 rounded-full" style={{ width: '71%' }}></div>
              </div>

              <div className="grid grid-cols-5 gap-4">
                {tabs.map((tab) => (
                  <div key={tab.id} className="text-center">
                    <div className={`w-8 h-8 rounded-full mx-auto mb-2 flex items-center justify-center text-white text-xs font-medium bg-${tab.color}-500`}>
                      {tab.progress}%
                    </div>
                    <p className="text-xs text-slate-600">{tab.label}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Tab Navigation */}
            <div className="bg-white rounded-xl border border-slate-900 overflow-hidden">
              <div className="border-b border-slate-200">
                <div className="flex">
                  {tabs.map((tab) => {
                    const Icon = tab.icon;
                    return (
                      <button
                        key={tab.id}
                        onClick={() => setActiveTab(tab.id)}
                        className={`flex-1 px-4 py-4 text-sm font-medium transition-colors relative ${
                          activeTab === tab.id
                            ? `text-${tab.color}-700 bg-${tab.color}-50 border-b-2 border-${tab.color}-500`
                            : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50'
                        }`}
                      >
                        <div className="flex items-center justify-center gap-2">
                          <Icon className="w-4 h-4" />
                          <span>{tab.label}</span>
                          <span className={`px-1.5 py-0.5 text-xs rounded-full ${
                            activeTab === tab.id
                              ? `bg-${tab.color}-100 text-${tab.color}-700`
                              : 'bg-slate-100 text-slate-600'
                          }`}>
                            {tab.count}
                          </span>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Tab Content */}
              <div className="p-6">
                {activeTab === 'overview' && <OverviewTab mockData={mockData} />}
                {activeTab === 'academic' && <AcademicTab mockData={mockData} />}
                {activeTab === 'activities' && <ActivitiesTab />}
                {activeTab === 'personal' && <PersonalTab />}
                {activeTab === 'teacher' && <TeacherTab />}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div className="col-span-4 space-y-6">
            {/* Student Overview Card */}
            <div className="bg-white rounded-xl border border-slate-900 p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-full bg-azure-100 flex items-center justify-center">
                  <span className="text-azure-700 font-semibold text-lg">AC</span>
                </div>
                <div>
                  <h3 className="font-semibold text-slate-900">{mockData.basicInfo.name}</h3>
                  <p className="text-sm text-slate-600">{mockData.basicInfo.gradeLevel} • {mockData.basicInfo.school}</p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-slate-600">GPA</span>
                  <p className="font-semibold text-slate-900">{mockData.basicInfo.gpa}</p>
                </div>
                <div>
                  <span className="text-slate-600">Class Rank</span>
                  <p className="font-semibold text-slate-900">{mockData.basicInfo.classRank} of {mockData.basicInfo.classSize}</p>
                </div>
                <div>
                  <span className="text-slate-600">SAT Score</span>
                  <p className="font-semibold text-slate-900">{mockData.basicInfo.testScores}</p>
                </div>
                <div>
                  <span className="text-slate-600">AP Courses</span>
                  <p className="font-semibold text-slate-900">{mockData.basicInfo.apCourses}</p>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-white rounded-xl border border-slate-900 p-6">
              <h3 className="font-semibold text-slate-900 mb-4">Quick Actions</h3>
              <div className="space-y-3">
                <button className="w-full p-3 text-left border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors flex items-center gap-3">
                  <Download className="w-4 h-4 text-azure-500" />
                  <span className="text-sm font-medium">Export PDF</span>
                </button>
                <button className="w-full p-3 text-left border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors flex items-center gap-3">
                  <Share2 className="w-4 h-4 text-teal-500" />
                  <span className="text-sm font-medium">Share with Teachers</span>
                </button>
                <button className="w-full p-3 text-left border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors flex items-center gap-3">
                  <Edit3 className="w-4 h-4 text-coral-500" />
                  <span className="text-sm font-medium">Edit Basic Info</span>
                </button>
              </div>
            </div>

            {/* Recommendations */}
            <div className="bg-white rounded-xl border border-slate-900 p-6">
              <h3 className="font-semibold text-slate-900 mb-4">Recommendations</h3>
              <div className="space-y-3">
                <div className="flex items-center gap-3 p-3 bg-azure-50 rounded-lg border border-azure-200">
                  <Sparkles className="w-4 h-4 text-azure-500" />
                  <div>
                    <p className="text-sm font-medium text-azure-900">Add more activities</p>
                    <p className="text-xs text-azure-700">You have room for 2 more activities</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 bg-gold-50 rounded-lg border border-gold-200">
                  <Trophy className="w-4 h-4 text-gold-500" />
                  <div>
                    <p className="text-sm font-medium text-gold-900">Highlight achievements</p>
                    <p className="text-xs text-gold-700">Consider adding more awards</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Tab Components
const OverviewTab = ({ mockData }) => (
  <div className="space-y-6">
    {/* Basic Information Section */}
    <div className="border border-slate-200 rounded-xl p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <User className="w-5 h-5 text-azure-500" />
          <h3 className="text-lg font-semibold text-slate-900">Basic Information</h3>
          <span className="px-2 py-1 bg-azure-100 text-azure-700 text-xs rounded-full">Auto-populated</span>
        </div>
        <button className="text-azure-600 hover:text-azure-700 text-sm font-medium flex items-center gap-1">
          <Edit3 className="w-3 h-3" />
          Edit
        </button>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Full Name</label>
          <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg text-slate-900">
            {mockData.basicInfo.name}
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Grade Level</label>
          <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg text-slate-900">
            {mockData.basicInfo.gradeLevel}
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">School</label>
          <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg text-slate-900">
            {mockData.basicInfo.school}
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Graduation Year</label>
          <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg text-slate-900">
            {mockData.basicInfo.graduationYear}
          </div>
        </div>
      </div>
    </div>

    {/* College Goals Section */}
    <div className="border border-slate-200 rounded-xl p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Target className="w-5 h-5 text-teal-500" />
          <h3 className="text-lg font-semibold text-slate-900">College Goals</h3>
        </div>
        <label className="flex items-center gap-2">
          <input type="checkbox" className="rounded border-slate-300" />
          <span className="text-sm text-slate-600">Exclude from brag sheet</span>
        </label>
      </div>

      <div className="mb-4">
        <p className="text-sm font-medium text-slate-700 mb-2">Quick Start - Choose a template:</p>
        <div className="flex flex-wrap gap-2">
          <button className="px-3 py-1 text-xs bg-azure-100 text-azure-700 rounded-full hover:bg-azure-200 transition-colors">
            Liberal Arts & Political Science
          </button>
          <button className="px-3 py-1 text-xs bg-teal-100 text-teal-700 rounded-full hover:bg-teal-200 transition-colors">
            Engineering & Innovation
          </button>
          <button className="px-3 py-1 text-xs bg-coral-100 text-coral-700 rounded-full hover:bg-coral-200 transition-colors">
            Pre-Med & Research
          </button>
        </div>
      </div>

      <textarea
        placeholder="I'm applying to mid-tier liberal arts colleges and state universities with strong political science programs..."
        rows={4}
        className="w-full p-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-azure-500 focus:border-azure-500"
      />
    </div>
  </div>
);

const AcademicTab = ({ mockData }) => (
  <div className="space-y-6">
    {/* Academic Stats Grid */}
    <div className="grid grid-cols-3 gap-4">
      <div className="bg-azure-50 border border-slate-900 rounded-xl p-6 text-center">
        <div className="text-azure-700 text-xs font-semibold uppercase tracking-wider mb-2">GPA</div>
        <div className="text-2xl font-bold text-slate-900 mb-1">{mockData.basicInfo.gpa}</div>
        <div className="text-sm text-slate-600">Weighted (4.0 scale)</div>
      </div>
      <div className="bg-teal-50 border border-slate-900 rounded-xl p-6 text-center">
        <div className="text-teal-700 text-xs font-semibold uppercase tracking-wider mb-2">Class Rank</div>
        <div className="text-2xl font-bold text-slate-900 mb-1">{mockData.basicInfo.classRank}</div>
        <div className="text-sm text-slate-600">of {mockData.basicInfo.classSize}</div>
      </div>
      <div className="bg-coral-50 border border-slate-900 rounded-xl p-6 text-center">
        <div className="text-coral-700 text-xs font-semibold uppercase tracking-wider mb-2">Test Scores</div>
        <div className="text-2xl font-bold text-slate-900 mb-1">{mockData.basicInfo.testScores}</div>
        <div className="text-sm text-slate-600">SAT/ACT</div>
      </div>
    </div>

    {/* Course Load Section */}
    <div className="border border-slate-200 rounded-xl p-6">
      <div className="flex items-center gap-2 mb-4">
        <BookOpen className="w-5 h-5 text-indigo-500" />
        <h3 className="text-lg font-semibold text-slate-900">Course Load & Academic Highlights</h3>
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">Advanced Courses</label>
          <input
            type="text"
            placeholder="e.g., 12 AP courses, 5 Honors courses"
            className="w-full p-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-azure-500 focus:border-azure-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-2">Academic Challenges</label>
          <textarea
            placeholder="Describe your most challenging academic experiences..."
            rows={3}
            className="w-full p-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-azure-500 focus:border-azure-500"
          />
        </div>
      </div>
    </div>
  </div>
);

const ActivitiesTab = () => (
  <div className="space-y-6">
    <div className="flex items-center justify-between">
      <div className="flex items-center gap-2">
        <Users className="w-5 h-5 text-indigo-500" />
        <h3 className="text-lg font-semibold text-slate-900">Extracurricular Activities</h3>
      </div>
      <button className="px-4 py-2 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 transition-colors flex items-center gap-2">
        <Plus className="w-4 h-4" />
        Add Activity
      </button>
    </div>

    {/* Activity Cards */}
    <div className="space-y-4">
      {[
        { name: 'Student Body President', years: '11-12', leadership: 'President', hours: '10 hrs/week' },
        { name: 'National Honor Society', years: '10-12', leadership: 'Secretary', hours: '5 hrs/week' },
        { name: 'Varsity Track & Field', years: '9-12', leadership: 'Team Captain', hours: '15 hrs/week' }
      ].map((activity, index) => (
        <div key={index} className="border border-slate-200 rounded-xl p-4 hover:border-slate-300 transition-colors">
          <div className="flex items-center justify-between mb-2">
            <h4 className="font-semibold text-slate-900">{activity.name}</h4>
            <span className="px-2 py-1 bg-indigo-100 text-indigo-700 text-xs rounded-full">{activity.years}</span>
          </div>
          <div className="grid grid-cols-2 gap-4 text-sm text-slate-600">
            <div>
              <span className="font-medium">Leadership Role:</span> {activity.leadership}
            </div>
            <div>
              <span className="font-medium">Time Commitment:</span> {activity.hours}
            </div>
          </div>
        </div>
      ))}
    </div>
  </div>
);

const PersonalTab = () => (
  <div className="space-y-6">
    {/* Personal Brand Section */}
    <div className="border border-slate-200 rounded-xl p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Heart className="w-5 h-5 text-coral-500" />
          <h3 className="text-lg font-semibold text-slate-900">In My Own Words</h3>
        </div>
        <label className="flex items-center gap-2">
          <input type="checkbox" className="rounded border-slate-300" />
          <span className="text-sm text-slate-600">Exclude from brag sheet</span>
        </label>
      </div>

      <textarea
        placeholder="In one paragraph, how would you describe yourself to a teacher who doesn't know you well?"
        rows={4}
        className="w-full p-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-coral-500 focus:border-coral-500"
      />
    </div>

    {/* Things You Might Not Know */}
    <div className="border border-slate-200 rounded-xl p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-gold-500" />
          <h3 className="text-lg font-semibold text-slate-900">Things You Might Not Know About Me</h3>
        </div>
        <button className="px-3 py-1 bg-gold-500 text-white rounded-lg hover:bg-gold-600 transition-colors text-sm flex items-center gap-1">
          <Plus className="w-3 h-3" />
          Add Detail
        </button>
      </div>

      <div className="mb-4">
        <p className="text-sm font-medium text-slate-700 mb-2">Quick Start:</p>
        <div className="flex flex-wrap gap-2">
          <button className="px-3 py-1 text-xs bg-gold-100 text-gold-700 rounded-full hover:bg-gold-200 transition-colors">
            I'm a first-generation college student
          </button>
          <button className="px-3 py-1 text-xs bg-coral-100 text-coral-700 rounded-full hover:bg-coral-200 transition-colors">
            I have a unique hobby/skill
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="p-4 bg-slate-50 rounded-lg">
          <h4 className="font-semibold text-slate-900 mb-2">I restore vintage electronics</h4>
          <p className="text-sm text-slate-600">This hobby has taught me patience and problem-solving skills...</p>
        </div>
      </div>
    </div>
  </div>
);

const TeacherTab = () => (
  <div className="space-y-6">
    <div className="bg-gold-50 border border-gold-200 rounded-xl p-4">
      <div className="flex items-center gap-2 mb-2">
        <GraduationCap className="w-5 h-5 text-gold-600" />
        <h3 className="font-semibold text-gold-900">Teacher-Specific Content</h3>
      </div>
      <p className="text-sm text-gold-700">This section will only appear on brag sheets shared with specific teachers.</p>
    </div>

    {/* Classroom Moments */}
    <div className="border border-slate-200 rounded-xl p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-slate-900">Specific Classroom Moments</h3>
        <button className="px-3 py-1 bg-azure-500 text-white rounded-lg hover:bg-azure-600 transition-colors text-sm flex items-center gap-1">
          <Plus className="w-3 h-3" />
          Add Moment
        </button>
      </div>

      <div className="space-y-4">
        <div className="p-4 bg-slate-50 rounded-lg">
          <h4 className="font-semibold text-slate-900 mb-2">Leading the Debate on Climate Policy</h4>
          <p className="text-sm text-slate-600">During our unit on environmental policy, I took the initiative to research carbon pricing mechanisms...</p>
        </div>
      </div>
    </div>

    {/* Thank You Note */}
    <div className="border border-slate-200 rounded-xl p-6">
      <div className="flex items-center gap-2 mb-4">
        <Heart className="w-5 h-5 text-coral-500" />
        <h3 className="text-lg font-semibold text-slate-900">Thank You Note</h3>
      </div>

      <textarea
        placeholder="A personal note thanking your teacher for their impact on your education..."
        rows={4}
        className="w-full p-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-coral-500 focus:border-coral-500"
      />
    </div>
  </div>
);

export default EnhancedBragSheetMockup;